import React, { useState, useEffect, useMemo } from "react";
import { FiSearch, FiDownload, FiRefreshCw, FiPackage, FiAlertCircle } from "react-icons/fi";
import { listProductos } from "../api/productos";
import { formatDate } from "../utils/fechas";
import { showAlertError } from "../utils/alertHelper";
import Loader from "./Loader";

const ReporteExistencias = () => {
    const [loading, setLoading] = useState(false);
    const [productos, setProductos] = useState([]);
    const [buscar, setBuscar] = useState("");

    // Paginación
    const PAGE_SIZE_OPTIONS = [10, 15, 25, 50, 100, 250, 500, 1000];
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const fetchProductos = async () => {
        setLoading(true);
        try {
            const response = await listProductos();
            if (response && response.data) {
                setProductos(response.data);
            }
        } catch (error) {
            console.error("Error cargando productos:", error);
            showAlertError("Error", "No se pudieron cargar los productos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProductos();
    }, []);

    // Filtrado por búsqueda
    const filteredData = useMemo(() => {
        if (!buscar.trim()) return productos;
        const q = buscar.toLowerCase();
        return productos.filter(p =>
            p.name?.toLowerCase().includes(q) ||
            p.sku?.toLowerCase().includes(q) ||
            p.number_part?.toLowerCase().includes(q) ||
            p.Categoria?.name?.toLowerCase().includes(q)
        );
    }, [productos, buscar]);

    // Paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginate = (p) => setCurrentPage(p);

    // Reset página al buscar
    const handleBuscar = (e) => {
        setBuscar(e.target.value);
        setCurrentPage(1);
    };

    // Cambiar tamaño de página
    const handlePageSize = (e) => {
        setItemsPerPage(Number(e.target.value));
        setCurrentPage(1);
    };

    // Totales siempre sobre TODOS los productos cargados (no filtrados)
    const totalStock = useMemo(() =>
        productos.reduce((sum, p) => {
            const stock = p.ExistenciaProductos?.reduce((s, e) => s + (e.stock || 0), 0) || 0;
            return sum + stock;
        }, 0),
        [productos]
    );

    const stockBajo = useMemo(() =>
        productos.filter(p => {
            const stock = p.ExistenciaProductos?.reduce((s, e) => s + (e.stock || 0), 0) || 0;
            return stock <= 5;
        }).length,
        [productos]
    );

    // Stock badge color
    const getStockBadge = (stock) => {
        if (stock === 0) return "bg-red-100 text-red-700 border border-red-200";
        if (stock <= 5) return "bg-orange-100 text-orange-700 border border-orange-200";
        if (stock <= 20) return "bg-yellow-100 text-yellow-700 border border-yellow-200";
        return "bg-emerald-100 text-emerald-700 border border-emerald-200";
    };

    // Exportar CSV — exporta TODOS los productos (sin filtro, sin paginación)
    const exportToExcel = () => {
        if (productos.length === 0) return;
        const headers = ["ID", "Nombre", "SKU", "N° Parte", "Categoría", "Tipo Ítem", "Unidad Medida", "Tributo", "Precio", "Stock", "Estado"];
        const rows = productos.map(p => {
            const stock = p.ExistenciaProductos?.reduce((s, e) => s + (e.stock || 0), 0) || 0;
            return [
                p.id,
                p.name,
                p.sku,
                p.number_part || "---",
                p.Categoria?.name || "---",
                p.TipoItem?.name || "---",
                p.UnidadMedida?.name || "---",
                p.Tributo?.name || "---",
                Number(p.price || 0).toFixed(2),
                stock,
                p.status === 1 ? "Activo" : "Inactivo"
            ];
        });
        const csvContent = "data:text/csv;charset=utf-8,\uFEFF"
            + headers.join(",") + "\n"
            + rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
        const link = document.createElement("a");
        link.setAttribute("href", encodeURI(csvContent));
        link.setAttribute("download", `Reporte_Existencias_${formatDate(new Date())}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8">
                <div className="flex justify-between items-start flex-wrap gap-3">
                    <div>
                        <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                            <FiPackage className="text-indigo-600" /> Reporte de Existencias
                        </h2>
                        <p className="text-slate-500 mt-1">
                            Listado de todos los productos y su stock actual en el sistema.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={fetchProductos}
                            disabled={loading}
                            className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 px-4 py-2 rounded-lg font-semibold transition-all active:scale-95 disabled:opacity-50"
                        >
                            <FiRefreshCw className={loading ? "animate-spin" : ""} /> Actualizar
                        </button>
                        {filteredData.length > 0 && (
                            <button
                                onClick={exportToExcel}
                                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg font-semibold transition-all shadow-md active:scale-95"
                            >
                                <FiDownload /> Exportar CSV
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                        <FiPackage size={22} />
                    </div>
                    <div>
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Total Productos</p>
                        <p className="text-2xl font-extrabold text-slate-800">{productos.length}</p>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-50 text-emerald-600 rounded-xl flex items-center justify-center">
                        <FiPackage size={22} />
                    </div>
                    <div>
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Unidades en Stock</p>
                        <p className="text-2xl font-extrabold text-slate-800">{totalStock.toLocaleString()}</p>
                    </div>
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex items-center gap-4">
                    <div className="w-12 h-12 bg-red-50 text-red-500 rounded-xl flex items-center justify-center">
                        <FiAlertCircle size={22} />
                    </div>
                    <div>
                        <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Stock Bajo (≤5 uds)</p>
                        <p className="text-2xl font-extrabold text-red-600">{stockBajo}</p>
                    </div>
                </div>
            </div>

            {/* Tabla */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 relative">
                {loading && (
                    <div className="absolute inset-0 bg-white/70 flex justify-center items-center z-10 rounded-2xl">
                        <Loader />
                    </div>
                )}

                {/* Barra de búsqueda + selector de registros */}
                <div className="p-5 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
                    <div className="relative max-w-sm flex-1 min-w-[200px]">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FiSearch className="text-slate-400" />
                        </div>
                        <input
                            type="text"
                            value={buscar}
                            onChange={handleBuscar}
                            placeholder="Buscar por nombre, SKU, categoría..."
                            className="block w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all bg-slate-50 focus:bg-white text-slate-700 text-sm"
                        />
                    </div>
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                        <span className="font-medium whitespace-nowrap">Mostrar</span>
                        <select
                            value={itemsPerPage}
                            onChange={handlePageSize}
                            className="border border-slate-300 rounded-lg px-3 py-2 bg-slate-50 text-slate-700 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all cursor-pointer"
                        >
                            {PAGE_SIZE_OPTIONS.map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                        <span className="font-medium whitespace-nowrap">por página</span>
                    </div>
                </div>

                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-sm text-left">
                        <thead className="text-xs text-slate-500 bg-slate-50 uppercase border-b border-slate-200">
                            <tr>
                                <th className="px-5 py-4 font-semibold">#</th>
                                <th className="px-5 py-4 font-semibold">Nombre</th>
                                <th className="px-5 py-4 font-semibold">SKU</th>
                                <th className="px-5 py-4 font-semibold">N° Parte</th>
                                <th className="px-5 py-4 font-semibold min-w-[160px]">Categoría</th>
                                <th className="px-5 py-4 font-semibold">Tipo Ítem</th>
                                <th className="px-5 py-4 font-semibold">Unidad</th>
                                <th className="px-5 py-4 font-semibold text-right">Precio</th>
                                <th className="px-5 py-4 font-semibold text-center min-w-[130px]">Stock</th>
                                <th className="px-5 py-4 font-semibold text-center">Estado</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {currentItems.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan={10} className="px-5 py-12 text-center text-slate-400 text-sm">
                                        {buscar ? "No se encontraron productos con ese criterio de búsqueda." : "No hay productos registrados."}
                                    </td>
                                </tr>
                            ) : (
                                currentItems.map((item, idx) => {
                                    const stock = item.ExistenciaProductos?.reduce((s, e) => s + (e.stock || 0), 0) || 0;
                                    return (
                                        <tr key={item.id} className="hover:bg-indigo-50/30 transition-colors">
                                            <td className="px-5 py-3.5 text-slate-400 text-xs">
                                                {indexOfFirstItem + idx + 1}
                                            </td>
                                            <td className="px-5 py-3.5 text-slate-800 font-semibold whitespace-nowrap">
                                                {item.name}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs font-mono">
                                                    {item.sku || "---"}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5 text-slate-500 text-xs font-mono">
                                                {item.number_part || "---"}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                {item.Categoria?.name
                                                    ? <span className="px-2 py-0.5 bg-purple-50 text-purple-700 border border-purple-100 rounded-full text-xs font-medium">
                                                        {item.Categoria.name}
                                                    </span>
                                                    : <span className="text-slate-400 text-xs">---</span>
                                                }
                                            </td>
                                            <td className="px-5 py-3.5 text-slate-600 text-xs whitespace-nowrap">
                                                {item.TipoItem?.name || "---"}
                                            </td>
                                            <td className="px-5 py-3.5 text-slate-600 text-xs whitespace-nowrap">
                                                {item.UnidadMedida?.name || "---"}
                                            </td>
                                            <td className="px-5 py-3.5 text-right font-bold text-slate-700">
                                                ${Number(item.price || 0).toFixed(2)}
                                            </td>
                                            <td className="px-5 py-3.5 text-center min-w-[130px]">
                                                <span className={`px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${getStockBadge(stock)}`}>
                                                    {stock.toLocaleString()} uds.
                                                </span>
                                            </td>
                                            <td className="px-5 py-3.5 text-center">
                                                {item.status === 1
                                                    ? <span className="px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-semibold">Activo</span>
                                                    : <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full text-xs font-semibold">Inactivo</span>
                                                }
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Paginación */}
                {totalPages > 1 && (
                    <div className="p-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-between">
                        <p className="text-xs text-slate-500">
                            Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredData.length)} de {filteredData.length} registros
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => paginate(currentPage - 1)}
                                disabled={currentPage === 1}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === 1
                                    ? 'text-slate-300 cursor-not-allowed'
                                    : 'text-slate-600 hover:bg-white hover:shadow-sm border border-slate-200'}`}
                            >
                                Anterior
                            </button>
                            {[...Array(totalPages)].map((_, i) => {
                                const pageNum = i + 1;
                                if (totalPages <= 7 || pageNum === 1 || pageNum === totalPages ||
                                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                                    return (
                                        <button
                                            key={i}
                                            onClick={() => paginate(pageNum)}
                                            className={`w-8 h-8 rounded-lg text-sm font-bold transition-all ${currentPage === pageNum
                                                ? 'bg-indigo-600 text-white shadow-md'
                                                : 'text-slate-600 hover:bg-white border border-transparent hover:border-slate-200'}`}
                                        >
                                            {pageNum}
                                        </button>
                                    );
                                } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                    return <span key={i} className="px-1 text-slate-400">...</span>;
                                }
                                return null;
                            })}
                            <button
                                onClick={() => paginate(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${currentPage === totalPages
                                    ? 'text-slate-300 cursor-not-allowed'
                                    : 'text-slate-600 hover:bg-white hover:shadow-sm border border-slate-200'}`}
                            >
                                Siguiente
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Leyenda de colores de stock */}
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
                <span className="flex items-center gap-1.5 text-slate-500"><span className="w-3 h-3 rounded-full bg-red-400 inline-block"></span> Sin stock</span>
                <span className="flex items-center gap-1.5 text-slate-500"><span className="w-3 h-3 rounded-full bg-orange-400 inline-block"></span> Stock crítico (1–5)</span>
                <span className="flex items-center gap-1.5 text-slate-500"><span className="w-3 h-3 rounded-full bg-yellow-400 inline-block"></span> Stock bajo (6–20)</span>
                <span className="flex items-center gap-1.5 text-slate-500"><span className="w-3 h-3 rounded-full bg-emerald-400 inline-block"></span> Stock normal (&gt;20)</span>
            </div>
        </div>
    );
};

export default ReporteExistencias;
